import { createSelector } from '@ngrx/store';
import { PayloadAction, createSlice } from 'ngrx-slice';

import {
  ExistingVipDiscountsRequest,
  ExistingVipDiscountsResponse,
  MerchantSearchResponse,
  PageableObject,
  PagedModelVipDiscountDto,
  ProductSortimentPageResponse,
  SortObject,
  VipDiscountCompanyDto,
  VipDiscountCreateRequest,
  VipDiscountCreateResponse,
  VipDiscountDto,
  VipDiscountListByCompanyResponse,
  VipDiscountUpdateRequest,
} from '@app/api/marketplace/models';
import { ProductVipDiscountToMerchant } from '@app/vip-discounts/modules/vip-discounts-selling/interfaces/product-discount-to-merchant.interface';

export interface ProductDiscount
  extends ProductSortimentPageResponse,
    VipDiscountDto {}

export interface ProductDiscountToMerchant {
  merchant: MerchantSearchResponse;
  discountList: Array<ProductDiscount>;
}

export const initialDataState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [],
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export interface State {
  partners: Array<MerchantSearchResponse>;
  selectedProducts: Array<ProductDiscount>;
  vipDiscounts: Array<ProductVipDiscountToMerchant>;
  discountsSaving: boolean;
  discountsSaveError: boolean;
  selectedProductsForGroupOperation: Array<string>;
  selectedGivenProductsForGroupOperation: Array<string>;
  // given discounts
  discountsGiven: Array<VipDiscountListByCompanyResponse>;
  discountsGivenLoading: boolean;
  discountsGivenLoadError: boolean;
  // given discounts paging + filtering
  givenCompanies: Array<VipDiscountCompanyDto>;
  givenCompaniesLoading: boolean;
  givenTotalElements: number;
  givenPageNumber: number;
  givenPageSize: number;
  givenCompanyIdFilter: Array<string>;
  givenProductNameFilter: string;
  deletedDiscountIndex: {
    tableIndex: number;
    rowIndex: number;
  };
  modifiedDiscountIndex: {
    tableIndex: number;
    rowIndex: number;
  };
  modifyFailedDiscountIndex: {
    tableIndex: number;
    rowIndex: number;
  };

  existingDiscountCombinations: ExistingVipDiscountsResponse;
  existingDiscountCombinationsLoading: boolean;
}

export const initialState: State = {
  partners: [],
  selectedProducts: [],
  vipDiscounts: [],
  discountsSaving: false,
  discountsSaveError: false,
  selectedProductsForGroupOperation: [],
  selectedGivenProductsForGroupOperation: [],
  // given discounts
  discountsGiven: [],
  discountsGivenLoading: false,
  discountsGivenLoadError: false,
  // given discounts paging + filtering
  givenCompanies: [],
  givenCompaniesLoading: false,
  givenTotalElements: 0,
  givenPageNumber: 0,
  givenPageSize: 25,
  givenCompanyIdFilter: [],
  givenProductNameFilter: '',
  deletedDiscountIndex: {
    tableIndex: -1,
    rowIndex: -1,
  },
  modifiedDiscountIndex: {
    tableIndex: -1,
    rowIndex: -1,
  },
  modifyFailedDiscountIndex: {
    tableIndex: -1,
    rowIndex: -1,
  },

  existingDiscountCombinations: {},
  existingDiscountCombinationsLoading: false,
};

// Groups a flat page of given VIP discounts by company, preserving the
// backend-provided ordering (companyName, packagingUnit.label), so the existing
// company-grouped table component can render a single page unchanged.
export function groupGivenDiscountsByCompany(
  content: Array<VipDiscountDto>
): Array<VipDiscountListByCompanyResponse> {
  const groups: Array<VipDiscountListByCompanyResponse> = [];
  const indexByCompany = new Map<string, number>();
  content.forEach((discount) => {
    const key = discount.companyName ?? '';
    let idx = indexByCompany.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByCompany.set(key, idx);
      groups.push({ companyName: discount.companyName, vipDiscountList: [] });
    }
    groups[idx].vipDiscountList!.push(discount);
  });
  return groups;
}

export const {
  actions: VipDiscountsSellingActions,
  selectors,
  ...VipDiscountsSellingFeature
} = createSlice({
  name: 'vipDiscountsSelling',
  initialState,
  reducers: {
    selectPartners: {
      trigger: (
        state,
        action: PayloadAction<{ partners: Array<MerchantSearchResponse> }>
      ) => {
        const partnersTemp = [...state.partners];
        action.partners.forEach((partner) => {
          if (
            !partnersTemp.find(
              (p) =>
                p.brandName === partner.brandName &&
                p.companyName === partner.companyName
            )
          ) {
            partnersTemp.push(partner);
          }
        });
        state.partners = partnersTemp;
        const discounts: Array<ProductVipDiscountToMerchant> = [];
        state.partners.forEach((merchant) => {
          discounts.push({
            merchant,
            vipDiscountList: state.selectedProducts.map(
              (product) =>
                ({
                  ...product,
                } as ProductDiscount)
            ),
          });
        });
        state.vipDiscounts = discounts;
      },
      success: () => {},
    },
    deselectPartner: {
      trigger: (
        state,
        action: PayloadAction<{ partner: MerchantSearchResponse }>
      ) => {
        state.partners = state.partners.filter(
          (p) =>
            p.brandName !== action.partner.brandName &&
            p.companyName !== action.partner.companyName
        );
        const discounts: Array<ProductVipDiscountToMerchant> = [];
        state.partners.forEach((merchant) => {
          discounts.push({
            merchant,
            vipDiscountList: state.selectedProducts.map(
              (product) =>
                ({
                  ...product,
                } as ProductDiscount)
            ),
          });
        });
        state.vipDiscounts = discounts;
      },
      success: () => {},
    },
    clearPartners: {
      trigger: (state) => {
        state.partners = [];
      },
      success: () => {},
    },
    clearIndexes: {
      trigger: (state) => {
        state.deletedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
        state.modifiedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
        state.modifyFailedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
      },
      success: () => {},
    },
    selectProducts: {
      trigger: (
        state,
        action: PayloadAction<{ products: Array<ProductSortimentPageResponse> }>
      ) => {
        state.selectedProducts = action.products;
        const discounts: Array<ProductVipDiscountToMerchant> = [];
        state.partners.forEach((merchant) => {
          discounts.push({
            merchant,
            vipDiscountList: state.selectedProducts.map(
              (product) =>
                ({
                  ...product,
                } as ProductDiscount)
            ),
          });
        });
        state.vipDiscounts = discounts;
      },
      success: () => {},
    },
    selectDiscountProduct: {
      trigger: (
        state,
        action: PayloadAction<{ merchantId: string; packagingUnitId: string }>
      ) => {
        const key = `${action.merchantId}#${action.packagingUnitId}`;
        const idx = state.selectedProductsForGroupOperation.indexOf(key);
        if (idx > -1) {
          state.selectedProductsForGroupOperation.splice(idx, 1);
        } else {
          state.selectedProductsForGroupOperation.push(key);
        }
      },
      success: () => {},
    },
    selectGivenDiscountProduct: {
      trigger: (state, action: PayloadAction<{ discountId: string }>) => {
        const idx = state.selectedGivenProductsForGroupOperation.indexOf(
          action.discountId
        );
        if (idx > -1) {
          state.selectedGivenProductsForGroupOperation.splice(idx, 1);
        } else {
          state.selectedGivenProductsForGroupOperation.push(action.discountId);
        }
      },
      success: () => {},
    },
    selectDiscountMerchant: {
      trigger: (
        state,
        action: PayloadAction<{
          merchantId: string;
          packagingUnitIds: Array<string>;
          select: boolean;
        }>
      ) => {
        action.packagingUnitIds.forEach((id) => {
          const key = `${action.merchantId}#${id}`;
          const idx = state.selectedProductsForGroupOperation.indexOf(key);
          if (idx > -1 && !action.select) {
            state.selectedProductsForGroupOperation.splice(idx, 1);
          } else if (idx === -1 && action.select) {
            state.selectedProductsForGroupOperation.push(key);
          }
        });
      },
      success: () => {},
    },
    selectGivenDiscountMerchant: {
      trigger: (
        state,
        action: PayloadAction<{
          discounts: Array<string>;
          select: boolean;
        }>
      ) => {
        action.discounts.forEach((id) => {
          const idx = state.selectedGivenProductsForGroupOperation.indexOf(id);
          if (idx > -1 && !action.select) {
            state.selectedGivenProductsForGroupOperation.splice(idx, 1);
          } else if (idx === -1 && action.select) {
            state.selectedGivenProductsForGroupOperation.push(id);
          }
        });
      },
      success: () => {},
    },
    selectDiscountAllMerchants: {
      trigger: (
        state,
        action: PayloadAction<{
          select: boolean;
        }>
      ) => {
        const keys = state.vipDiscounts.flatMap((discounts) =>
          discounts.vipDiscountList.map(
            (discount) =>
              `${discounts.merchant.merchantId}#${discount.packagingUnitId}`
          )
        );
        keys.forEach((key) => {
          const idx = state.selectedProductsForGroupOperation.indexOf(key);
          if (idx > -1 && !action.select) {
            state.selectedProductsForGroupOperation.splice(idx, 1);
          } else if (idx === -1 && action.select) {
            state.selectedProductsForGroupOperation.push(key);
          }
        });
      },
      success: () => {},
    },
    selectGivenDiscountAllMerchants: {
      trigger: (
        state,
        action: PayloadAction<{
          select: boolean;
        }>
      ) => {
        const keys = state.discountsGiven.flatMap((discounts) =>
          discounts?.vipDiscountList?.map((discount) => discount.discountId)
        );
        keys.forEach((key) => {
          const idx = state.selectedGivenProductsForGroupOperation.indexOf(
            key!
          );
          if (idx > -1 && !action.select) {
            state.selectedGivenProductsForGroupOperation.splice(idx, 1);
          } else if (idx === -1 && action.select) {
            state.selectedGivenProductsForGroupOperation.push(key!);
          }
        });
      },
      success: () => {},
    },
    clearSelectedProductsForGroupOperation: {
      trigger: (state) => {
        state.selectedProductsForGroupOperation = [];
      },
      success: () => {},
    },
    saveDiscounts: {
      trigger: (
        state,
        _action: PayloadAction<{
          request: VipDiscountCreateRequest;
        }>
      ) => {
        state.discountsSaving = true;
        state.discountsSaveError = false;
      },
      success: (
        state,
        _action: PayloadAction<{
          response: VipDiscountCreateResponse;
        }>
      ) => {
        state.discountsSaving = false;
      },
      failure: (state) => ({
        ...state,
        discountsSaving: false,
        discountsSaveError: true,
      }),
    },
    loadDiscountsGiven: {
      trigger: (
        state,
        action: PayloadAction<{
          page: number;
          size: number;
          companyIds?: Array<string>;
          productName?: string;
        }>
      ) => {
        state.discountsGiven = [];
        state.selectedGivenProductsForGroupOperation = [];
        state.discountsGivenLoading = true;
        state.givenPageNumber = action.page;
        state.givenPageSize = action.size;
        state.givenCompanyIdFilter = action.companyIds ?? [];
        state.givenProductNameFilter = action.productName ?? '';
      },
      success: (
        state,
        action: PayloadAction<{
          response: PagedModelVipDiscountDto;
        }>
      ) => {
        state.discountsGiven = groupGivenDiscountsByCompany(
          action.response.content ?? []
        );
        state.givenTotalElements = action.response.page?.totalElements ?? 0;
        state.givenPageNumber =
          action.response.page?.number ?? state.givenPageNumber;
        state.givenPageSize = action.response.page?.size ?? state.givenPageSize;
        state.discountsGivenLoading = false;
        state.discountsGivenLoadError = false;
      },
      failure: (state) => ({
        ...state,
        discountsGivenLoading: false,
        discountsGivenLoadError: true,
      }),
    },
    loadGivenCompanies: {
      trigger: (state) => {
        state.givenCompaniesLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          companies: Array<VipDiscountCompanyDto>;
        }>
      ) => {
        state.givenCompanies = action.companies;
        state.givenCompaniesLoading = false;
      },
      failure: (state) => ({
        ...state,
        givenCompaniesLoading: false,
      }),
    },
    clearDiscountsGiven: {
      trigger: (state) => {
        state.discountsGiven = [];
      },
      success: () => {},
    },
    editDiscount: {
      trigger: (
        state,
        _action: PayloadAction<{
          request: {
            vipDiscountId: string;
            body: VipDiscountUpdateRequest;
          };
          index: {
            tableIndex: number;
            rowIndex: number;
          };
        }>
      ) => {
        state.modifiedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
        state.modifyFailedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
      },
      success: (
        state,
        action: PayloadAction<{
          index: {
            tableIndex: number;
            rowIndex: number;
          };
        }>
      ) => {
        state.modifiedDiscountIndex = action.index;
      },
      failure: (
        state,
        action: PayloadAction<{
          index: {
            tableIndex: number;
            rowIndex: number;
          };
        }>
      ) => ({
        ...state,
        modifyFailedDiscountIndex: action.index,
      }),
    },
    findExistingDiscountCombinations: {
      trigger: (
        state,
        _action: PayloadAction<{ request: ExistingVipDiscountsRequest }>
      ) => {
        state.existingDiscountCombinations = {};
        state.existingDiscountCombinationsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: ExistingVipDiscountsResponse }>
      ) => {
        state.existingDiscountCombinations = action.response;
        state.existingDiscountCombinationsLoading = false;
      },
      failure: (state) => ({
        ...state,
        existingDiscountCombinationsLoading: false,
      }),
    },
    deleteDiscountFromStore: {
      trigger: (
        state,
        action: PayloadAction<{
          merchantId: string;
          packagingUnitId: string;
        }>
      ) => {
        const discountListLength = state.vipDiscounts.find(
          (discounts) => discounts.merchant.merchantId === action.merchantId
        )?.vipDiscountList.length;
        if ((discountListLength ?? 0) > 1) {
          state.vipDiscounts = state.vipDiscounts.map((discounts) =>
            discounts.merchant.merchantId === action.merchantId
              ? {
                  ...discounts,
                  vipDiscountList: [
                    ...discounts.vipDiscountList.filter(
                      (discount) =>
                        discount.packagingUnitId !== action.packagingUnitId
                    ),
                  ],
                }
              : discounts
          );
        } else {
          state.vipDiscounts = state.vipDiscounts.filter(
            (discounts) => discounts.merchant.merchantId !== action.merchantId
          );
        }
        state.selectedProductsForGroupOperation =
          state.selectedProductsForGroupOperation.filter(
            (product) =>
              product !== `${action.merchantId}#${action.packagingUnitId}`
          );
      },
      success: (_state) => {},
    },
    deleteDiscount: {
      trigger: (
        state,
        _action: PayloadAction<{
          discountId: string;
          index: {
            tableIndex: number;
            rowIndex: number;
          };
        }>
      ) => {
        state.deletedDiscountIndex = {
          tableIndex: -1,
          rowIndex: -1,
        };
      },
      success: (
        state,
        action: PayloadAction<{
          index: {
            tableIndex: number;
            rowIndex: number;
          };
        }>
      ) => {
        state.deletedDiscountIndex = action.index;
      },
      failure: () => {},
    },
    deleteSelectedDiscounts: {
      trigger: (
        state,
        action: PayloadAction<{
          selectedDiscounts: Array<string>;
        }>
      ) => {
        state.selectedGivenProductsForGroupOperation =
          state.selectedGivenProductsForGroupOperation.filter(
            (product) => action.selectedDiscounts.indexOf(product) < 0
          );
      },
      success: () => {},
      failure: () => {},
    },
    reset: (_) => initialState,
  },
});

export const VipDiscountsSellingSelectors = {
  ...selectors,
  isMerchantAllProductsSelected: (merchantIdx: number) =>
    createSelector(
      selectors.selectVipDiscounts,
      selectors.selectSelectedProductsForGroupOperation,
      (
        discountToMerchantList,
        selection
      ): 'CHECKED' | 'INDETERMINATE' | 'NOT_CHECKED' => {
        const merchantDiscount = discountToMerchantList[merchantIdx];
        if (merchantDiscount) {
          const keys = merchantDiscount.vipDiscountList.map(
            (product) =>
              `${merchantDiscount.merchant.merchantId}#${product.packagingUnitId}`
          );
          if (keys.every((key) => selection.includes(key))) {
            return 'CHECKED';
          } else if (keys.some((key) => selection.includes(key))) {
            return 'INDETERMINATE';
          } else {
            return 'NOT_CHECKED';
          }
        } else {
          return 'NOT_CHECKED';
        }
      }
    ),
  isMerchantAllGivenProductsSelected: (merchantIdx: number) =>
    createSelector(
      selectors.selectDiscountsGiven,
      selectors.selectSelectedGivenProductsForGroupOperation,
      (
        discountToMerchantList,
        selection
      ): 'CHECKED' | 'INDETERMINATE' | 'NOT_CHECKED' => {
        const merchantDiscount = discountToMerchantList[merchantIdx];
        if (merchantDiscount) {
          const keys = merchantDiscount?.vipDiscountList?.map(
            (product) => product.discountId
          );
          if (keys?.every((key) => selection.includes(key!))) {
            return 'CHECKED';
          } else if (keys?.some((key) => selection.includes(key!))) {
            return 'INDETERMINATE';
          } else {
            return 'NOT_CHECKED';
          }
        } else {
          return 'NOT_CHECKED';
        }
      }
    ),
  isAllMerchantsSelected: createSelector(
    selectors.selectVipDiscounts,
    selectors.selectSelectedProductsForGroupOperation,
    (
      discountToMerchantList,
      selection
    ): 'CHECKED' | 'INDETERMINATE' | 'NOT_CHECKED' => {
      if (
        discountToMerchantList.flatMap((merchant) => merchant.vipDiscountList)
          .length === selection.length &&
        selection.length !== 0
      ) {
        return 'CHECKED';
      } else if (selection.length === 0) {
        return 'NOT_CHECKED';
      } else {
        return 'INDETERMINATE';
      }
    }
  ),

  isAllGivenMerchantsSelected: createSelector(
    selectors.selectDiscountsGiven,
    selectors.selectSelectedGivenProductsForGroupOperation,
    (
      discountToMerchantList,
      selection
    ): 'CHECKED' | 'INDETERMINATE' | 'NOT_CHECKED' => {
      if (
        discountToMerchantList.flatMap((merchant) => merchant.vipDiscountList)
          .length === selection.length &&
        selection.length !== 0
      ) {
        return 'CHECKED';
      } else if (selection.length === 0) {
        return 'NOT_CHECKED';
      } else {
        return 'INDETERMINATE';
      }
    }
  ),
};
